import { useEffect, useState } from "react";
import PageContainer from "../components/admin/PageContainer";
import CollapseCard from "../components/admin/CollapseCard";
import Modal from "../components/admin/Modal";
import { useToast } from "../components/toast/ToastProvider";
import Main from "../components/admin/Main";
import { createSlideShow, deleteSlideShow, fetchAllSlideShow, updateSlideShow } from "../service/Slide";
import SliderForm from "../components/admin/SliderForm";
import SliderList from "../components/admin/SliderList";

const Sliders = () => {
    const [slides, setSlides] = useState([]);
    const [selectedSlide, setSelectedSlide] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const loadSlides = async () => {
        try {
            setLoading(true);
            const res = await fetchAllSlideShow();
            setSlides(res);
        } catch (error) {
            toast("Error fetching slides", "ERROR");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSlides();
    }, []);

    const handleSave = async (slideData) => {
        try {
            const formData = new FormData();
            formData.append("title", slideData.title);
            formData.append("name", slideData.name);
            formData.append("description", slideData.description);
            formData.append("type", slideData.type);
            formData.append("bgColor1", slideData.bgColor1);
            formData.append("bgColor2", slideData.bgColor2);
            formData.append("titleColor", slideData.titleColor);
            formData.append("nameColor", slideData.nameColor);
            formData.append("descriptionColor", slideData.descriptionColor);
            formData.append("linkUrl", slideData.linkUrl);

            if (slideData.imageUrl instanceof File) {
                formData.append("imageUrl", slideData.imageUrl);
            } else if (typeof slideData.imageUrl === "string" && slideData.imageUrl) {
                formData.append("imageUrl", slideData.imageUrl);
            }

            if (slideData.id) {
                await updateSlideShow(slideData.id, formData);
                toast("Slide updated", "SUCCESS");

                // ✅ update local state immediately
                setSlides((prev) =>
                    prev.map((s) =>
                        s.id === slideData.id
                            ? { ...s, ...slideData, imageUrl: slideData.imageUrl || s.imageUrl }
                            : s
                    )
                );
            } else {
                await createSlideShow(formData);
                toast("Slide added", "SUCCESS");
                await loadSlides();
            }

            setShowModal(false);
            setSelectedSlide(null);
            setViewMode(false);
        } catch (error) {
            toast("Error saving slide", "ERROR");
        }
    };

    const handleEdit = (slide) => {
        setSelectedSlide(slide);
        setViewMode(false);
        setShowModal(true);
    };

    const handleView = (slide) => {
        setSelectedSlide(slide);
        setViewMode(true);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this slide?")) {
            try {
                await deleteSlideShow(id);
                toast("Slide deleted", "SUCCESS");
                await loadSlides();
            } catch (error) {
                toast("Error deleting slide", "ERROR");
            }
        }
    };

    return (
        <Main>
            <PageContainer
                title="Slides"
                buttonText={loading ? "Loading..." : "ADD"}
                onButtonClick={() => {
                    setSelectedSlide(null);
                    setViewMode(false);
                    setShowModal(true);
                }}
            >
                {/* ✅ Modal for Add/Edit/View */}
                <Modal
                    visible={showModal}
                    size="lg"
                    onClose={() => {
                        setShowModal(false);
                        setSelectedSlide(null);
                        setViewMode(false);
                    }}
                    headerText={
                        viewMode
                            ? "View Slide"
                            : selectedSlide
                                ? "Edit Slide"
                                : "Add Slide"
                    }
                    centered
                >
                    <SliderForm
                        initialData={selectedSlide}
                        readOnly={viewMode}
                        onSave={handleSave}
                        loading={loading}
                        setLoading={setLoading}
                    />

                    {viewMode && (
                        <div className="d-flex justify-content-end mt-3">
                            <button
                                className="btn btn-success"
                                onClick={() => {
                                    setLoading(true);
                                    setTimeout(() => {
                                        setViewMode(false);
                                        setLoading(false);
                                    }, 200);
                                }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Loading...
                                    </>
                                ) : (
                                    "Edit"
                                )}
                            </button>
                        </div>
                    )}
                </Modal>

                {/* ✅ List of slides */}
                <CollapseCard title="Slides List">
                    <SliderList
                        slides={slides}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onView={handleView}
                    />
                </CollapseCard>
            </PageContainer>
        </Main>
    );
};

export default Sliders;
